import React, { useMemo } from 'react';
import Select from 'react-select';
import './EnvironmentalStatusSettings.css';
import {
  ENVIRONMENTAL_STATUS_ICON_TYPES,
  createEmptyEnvironmentalStatusMetric,
  inferEnvironmentalMetricKind,
} from '../widgets/environmentalStatusConfig';
import { DeleteIcon } from '../svg/UtilsIcons';

const EnvironmentalStatusSettings = ({
  edit,
  setEdit,
  devices = [],
  validDatastreams = [],
  deviceOptions = [],
}) => {
  const props = edit?.props || {};
  const metrics = Array.isArray(props.metrics) ? props.metrics : [];
  const hasDevices = Array.isArray(devices) && devices.length > 0;

  const resolvedDeviceOptions = useMemo(() => {
    if (Array.isArray(deviceOptions) && deviceOptions.length > 0) {
      return deviceOptions;
    }

    return (devices || []).map((device) => ({
      value: Number(device?.id) || 0,
      label: device?.name || `Device ${device?.id ?? ''}`,
    }));
  }, [deviceOptions, devices]);

  const updateProps = (patch) =>
    setEdit((prev) => ({
      ...prev,
      props: {
        ...(prev.props || {}),
        ...patch,
      },
    }));

  const updateMetric = (metricId, patch) =>
    setEdit((prev) => ({
      ...prev,
      props: {
        ...(prev.props || {}),
        metrics: ((prev.props?.metrics || [])).map((metric) =>
          metric?.id === metricId
            ? {
                ...metric,
                ...patch,
              }
            : metric
        ),
      },
    }));

  const removeMetric = (metricId) =>
    setEdit((prev) => ({
      ...prev,
      props: {
        ...(prev.props || {}),
        metrics: ((prev.props?.metrics || [])).filter((metric) => metric?.id !== metricId),
      },
    }));

  const getDatastreamList = (metric) => {
    if (!hasDevices) return (validDatastreams || []).filter(Boolean);

    const streamDevice = (devices || []).find(
      (device) => Number(device?.id) === Number(metric?.deviceId)
    );

    return (streamDevice?.template?.datastreams || []).filter(Boolean);
  };

  const getDatastreamOptions = (metric) =>
    getDatastreamList(metric).map((datastream) => ({
      value: Number(datastream?.id),
      label: `${datastream?.name || 'Datastream'} [${datastream?.pin || '-'}]`,
    }));

  return (
    <div className="ess-settings">
      <button
        type="button"
        className="details-add-btn"
        style={{ width: '220px' }}
        onClick={() => {
          updateProps({
            metrics: [...metrics, createEmptyEnvironmentalStatusMetric(metrics.length)],
          });
        }}
      >
        <span>+</span>
        <span>Add Datastream</span>
      </button>

      <div className="ess-grid">
        {metrics.map((metric, index) => {
          const datastreamOptions = getDatastreamOptions(metric);
          const isEnabled = typeof metric?.enabled === 'boolean' ? metric.enabled : true;
          const caption =
            metric?.iconType === 'auto' || !metric?.iconType
              ? inferEnvironmentalMetricKind(metric?.label, metric?.unit).toUpperCase()
              : String(metric.iconType).toUpperCase();

          return (
            <div
              key={metric?.id || `metric-${index}`}
              className={`ess-card ${isEnabled ? 'is-enabled' : 'is-disabled'}`}
            >
              <div className="ess-cardHeader">
                <div className="ess-cardHeading">
                  <span className="ess-cardTitle">
                    {metric?.label || `Datastream ${index + 1}`}
                  </span>
                  <span className="ess-cardCaption">{caption}</span>
                </div>

                <div className="ess-cardActions">
                  <button
                    type="button"
                    className={`ess-toggle ${isEnabled ? 'is-on' : ''}`}
                    aria-pressed={isEnabled}
                    onClick={() => updateMetric(metric.id, { enabled: !isEnabled })}
                  >
                    <span className="ess-toggleThumb" />
                  </button>

                  <button
                    type="button"
                    className="details-delete-btn ess-deleteBtn"
                    style={{ position: 'static' }}
                    onClick={() => removeMetric(metric.id)}
                  >
                    <DeleteIcon />
                  </button>
                </div>
              </div>

              <div className="ess-cardBody">
                <div className="form-item ess-formItem">
                  <label className="form-lbl ess-formLabel">Label</label>
                  <input
                    type="text"
                    className="add-column"
                    value={metric?.label || ''}
                    onChange={(e) => updateMetric(metric.id, { label: e.target.value })}
                  />
                </div>

                <div className="form-item ess-formItem">
                  <label className="form-lbl ess-formLabel">Icon</label>
                  <Select
                    classNamePrefix="react-select"
                    options={ENVIRONMENTAL_STATUS_ICON_TYPES}
                    value={ENVIRONMENTAL_STATUS_ICON_TYPES.find(
                      (option) => option.value === (metric?.iconType || 'auto')
                    )}
                    onChange={(selectedOption) =>
                      updateMetric(metric.id, { iconType: selectedOption?.value || 'auto' })
                    }
                    isClearable={false}
                  />
                </div>

                {hasDevices ? (
                  <div className="form-item ess-formItem">
                    <label className="form-lbl ess-formLabel">Device</label>
                    <Select
                      classNamePrefix="react-select"
                      options={resolvedDeviceOptions}
                      value={
                        resolvedDeviceOptions.find(
                          (option) => Number(option.value) === Number(metric?.deviceId)
                        ) || null
                      }
                      onChange={(selectedOption) =>
                        updateMetric(metric.id, {
                          deviceId: selectedOption ? Number(selectedOption.value) : 0,
                          dataId: null,
                          pin: '',
                        })
                      }
                      isClearable={true}
                    />
                  </div>
                ) : null}

                <div className="form-item ess-formItem">
                  <label className="form-lbl ess-formLabel">Datastream</label>
                  <Select
                    classNamePrefix="react-select"
                    options={datastreamOptions}
                    value={
                      datastreamOptions.find(
                        (option) => Number(option.value) === Number(metric?.dataId)
                      ) || null
                    }
                    onChange={(selectedOption) => {
                      const selectedDatastream = getDatastreamList(metric).find(
                        (datastream) => Number(datastream?.id) === Number(selectedOption?.value)
                      );

                      updateMetric(metric.id, {
                        dataId: selectedOption ? Number(selectedOption.value) : null,
                        pin: selectedDatastream?.pin || '',
                        label: selectedDatastream?.name || metric?.label || '',
                        unit:
                          selectedDatastream?.unit !== undefined &&
                          selectedDatastream?.unit !== null &&
                          String(selectedDatastream.unit).trim() !== ''
                            ? selectedDatastream.unit
                            : metric?.unit || '',
                        defaultValue:
                          selectedDatastream?.defaultValue !== undefined
                            ? selectedDatastream.defaultValue
                            : metric?.defaultValue ?? '',
                      });
                    }}
                    isDisabled={hasDevices && !metric?.deviceId}
                    isClearable={true}
                  />
                </div>

                <div className="form-item ess-formItem">
                  <label className="form-lbl ess-formLabel">Default Value</label>
                  <input
                    type="text"
                    className="add-column"
                    value={metric?.defaultValue ?? ''}
                    onChange={(e) => updateMetric(metric.id, { defaultValue: e.target.value })}
                  />
                </div>

                <div className="form-item ess-formItem">
                  <label className="form-lbl ess-formLabel">Unit</label>
                  <input
                    type="text"
                    className="add-column"
                    value={metric?.unit || ''}
                    onChange={(e) => updateMetric(metric.id, { unit: e.target.value })}
                  />
                </div>

                <div className="form-item ess-formItem">
                  <label className="form-lbl ess-formLabel">Decimals</label>
                  <input
                    type="number"
                    className="add-column"
                    min={0}
                    max={6}
                    value={Number(metric?.decimals ?? 0)}
                    onChange={(e) =>
                      updateMetric(metric.id, {
                        decimals: Math.max(0, Number(e.target.value || 0)),
                      })
                    }
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="ess-sizeGrid">
        <div className="form-item ess-formItem">
          <label className="form-lbl ess-formLabel">Title Size</label>
          <input
            type="number"
            className="add-column"
            min={12}
            max={48}
            value={Number(props.titleSize ?? 20)}
            onChange={(e) => updateProps({ titleSize: Number(e.target.value || 20) })}
          />
        </div>

        <div className="form-item ess-formItem">
          <label className="form-lbl ess-formLabel">Datastream Title Size</label>
          <input
            type="number"
            className="add-column"
            min={10}
            max={32}
            value={Number(props.datastreamTitleSize ?? 14)}
            onChange={(e) => updateProps({ datastreamTitleSize: Number(e.target.value || 14) })}
          />
        </div>

        <div className="form-item ess-formItem">
          <label className="form-lbl ess-formLabel">Value Size</label>
          <input
            type="number"
            className="add-column"
            min={24}
            max={120}
            value={Number(props.valueSize ?? 72)}
            onChange={(e) => updateProps({ valueSize: Number(e.target.value || 72) })}
          />
        </div>

        <div className="form-item ess-formItem">
          <label className="form-lbl ess-formLabel">Border Radius</label>
          <input
            type="number"
            className="add-column"
            min={0}
            max={48}
            value={Number(props.borderRadius ?? 24)}
            onChange={(e) => updateProps({ borderRadius: Number(e.target.value || 0) })}
          />
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalStatusSettings;
